import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSeo, breadcrumbLd } from "@/lib/seo";
import { EDITION } from "@/lib/edition";
import { ArrowLeft, MagnifyingGlass, X } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ShareButton from "@/components/ShareButton";
import TalkVibes from "@/components/TalkVibes";

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [slug]);
  const _ok = a && a !== false;
  useSeo(_ok ? a.title : "Article", _ok ? a.summary : null, _ok ? a.cover_url : null,
    _ok ? {
      canonicalPath: `/articles/${a.slug}`,
      type: "article",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: a.title,
          description: a.summary || undefined,
          ...(a.cover_url ? { image: a.cover_url } : {}),
          ...(a.author ? { author: { "@type": "Person", name: a.author } } : {}),
          ...(a.published_at || a.created_at ? { datePublished: a.published_at || a.created_at } : {}),
          publisher: { "@type": "Organization", name: EDITION.brand },
          mainEntityOfPage: `${EDITION.url}/articles/${a.slug}`,
        },
        breadcrumbLd([
          ["Home", "/"],
          ["Articles", "/articles"],
          [a.title, `/articles/${a.slug}`],
        ]),
      ],
    } : undefined);

  if (a === null) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (a === false) return <div className="p-16 text-center text-muted-foreground">Article not found.</div>;

  return (
    <article className="relative mx-auto max-w-3xl px-5 py-14">
      <div className="mb-6 flex items-center justify-between">
        <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> All articles</Link>
        <button onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/articles"))} aria-label="Close" data-testid="article-close-btn" className="grid h-9 w-9 place-items-center rounded-full border border-border/60 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"><X size={18} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">{(a.tags || []).map((t) => <Badge key={t} variant="secondary" className="rounded-full font-normal">{t}</Badge>)}</div>
      <h1 className="mt-3 font-heading text-4xl font-light leading-tight tracking-tight">{a.title}</h1>
      <p className="mt-3 text-sm text-muted-foreground">By {a.author}</p>
      <div className="mt-4"><ShareButton entityType="article" entityId={a.slug || a.id} targetPath={`/articles/${a.slug}`} title={a.title} label="Share article" /></div>
      {a.cover_url && <img src={a.cover_url} alt={a.title} className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover" />}
      <div className="prose mt-8 max-w-none">
        {(a.body || "").split("\n\n").map((block, i) => {
          const lines = block.split("\n").filter(Boolean);
          if (lines.length && lines.every((l) => l.startsWith("- "))) {
            return <ul key={i} className="mb-5 list-disc space-y-1.5 pl-5 text-foreground/90">{lines.map((l, j) => <li key={j} className="leading-relaxed">{l.slice(2)}</li>)}</ul>;
          }
          if (block.startsWith("## ")) {
            const [head, ...rest] = block.split("\n");
            const para = rest.join("\n").trim();
            return (
              <div key={i}>
                <h2 className="mb-3 mt-9 font-heading text-2xl font-light tracking-tight">{head.slice(3)}</h2>
                {para && <p className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90">{para}</p>}
              </div>
            );
          }
          return <p key={i} className="mb-4 whitespace-pre-line leading-relaxed text-foreground/90">{block}</p>;
        })}
      </div>
      <div className="mt-10 rounded-2xl border border-secondary/25 bg-accent/50 p-6 text-center">
        <p className="font-heading text-lg text-primary">Ready to reconnect?</p>
        <p className="mt-1 text-sm text-muted-foreground">Post a search and let our worldwide alumni community help you find them.</p>
        <Link to="/find"><Button className="mt-4 rounded-full gap-1.5" data-testid="article-find-cta"><MagnifyingGlass size={16} /> Start a Lost Colleague search</Button></Link>
      </div>
      <div className="mt-8">
        <TalkVibes entityType="article" entityId={a.slug || a.id} title={a.title}
          subtitle="Remember something we missed? Add a memory or ask the community." accent="article" />
      </div>
    </article>
  );
}
